//****************************************
// Graph Tools Element
// DOM Registration: No
// National Instruments Copyright 2017
//****************************************

/**
## ni-graph-tools

> A custom element that provides a graph tools "legend".

Use it to add navigation controls to a graph, chart or intensity graph element.
It provides buttons to switch between pan mode, box zoom mode or to lock navigation.

It also provides a resetOffset button which resets the axes to the range of the data.

Example:
```html
<ni-cartesian-graph graph-ref="g1" value="[[2, 3, 5, 7, 11, 13, 17, 19],
                          [0, 1, 1, 2, 3, 5, 8, 13]]">
  <ni-cartesian-axis show label="Index" axis-position="bottom"></ni-cartesian-axis>
  <ni-cartesian-axis show label="Value" axis-position="left"></ni-cartesian-axis>
</ni-cartesian-graph>

<ni-graph-tools graph-ref="g1" mode="pan"></ni-graph-tools>
```
*/

(function() {
    'use strict';

    var ModeEnum = Object.freeze({
        LOCKED: 'locked',
        PAN: 'pan',
        ZOOM: 'zoom'
    });

    var MODE_ENUM = ModeEnum;
    var $ = NationalInstruments.Globals.jQuery;
    var NI_SUPPORT = NationalInstruments.HtmlVI.NISupport;

    var navigationStates = {};
    navigationStates[MODE_ENUM.LOCKED] = [true, false, false];
    navigationStates[MODE_ENUM.PAN] = [false, true, false];
    navigationStates[MODE_ENUM.ZOOM] = [false, false, true];

    class GraphTools extends NationalInstruments.HtmlVI.Elements.GraphLegend {
        /**### ni-graph-tools properties*/

        /// include ./ni-graph-legend.js
        static get properties() {
            const props = Object.assign({}, super.properties, {
                /** **mode** (string) - the mode of the graph tools. Default: 'locked'.
                Accepted values: 'locked', 'pan', 'zoom'*/
                'mode': {
                    value: MODE_ENUM.LOCKED,
                    type: 'string'
                }
            });

            return props;
        }

        /**### ni-graph-tools methods*/
        created() {
            super.created();
            this._parentGraph = undefined;
            this.lockButton = null;
            this.panButton = null;
            this.zoomButton = null;
            this.zoomOutButton = null;
            this.toggleAutoScaleButton = null;
            this.snapshotButton = null;
            this.rangeMismatch = true;
            this.uihelpers = new NationalInstruments.HtmlVI.Elements.LegendHelpers(this.helpers, null);
            this.autoScaleStates = {};
            this._eventHandlers = [];
        };

        addTrackedEventHandler (element, eventName, eventHandler) {
            element.addEventListener(eventName, eventHandler);
            this._eventHandlers.push({
                element: element,
                event: eventName,
                handler: eventHandler
            });
        }

        removeTrackedEventHandler (element, eventName, eventHandler) {
            element.removeEventListener(eventName, eventHandler);
            var index = this._eventHandlers.findIndex(i => element === i.element && eventName === i.event && eventHandler === i.handler);
            if (index > -1) {
                this._eventHandlers.splice(index, 1);
            }
        }

        removeTrackedEventHandlers () {
            this._eventHandlers.forEach(trackedEvent => {
                trackedEvent.element.removeEventListener(trackedEvent.event, trackedEvent.handler);
            });

            this._eventHandlers = [];
        }

        ready () {
            super.ready();
            this.addListeners();
        }

        attachToGraph(graphElement) {
            if (graphElement && graphElement.isReady) {
                this._parentGraph = graphElement;
                graphElement.registerGraphTools(this);
            }
        };

        /* The result of user interactions (pan, zoom, ..) are often modified axes
        parameters in the flot datastructures.
        When needed, call this function to update the ni-cartesian-axis elements to have te new values */
        updateCartesianAxesFromFlot() {
            this._parentGraph.axes.forEach(function(axis) {
                if (axis instanceof JQX.CartesianAxis) {
                    axis.syncWithFlot();
                }
            });
            this.rangeMismatch = false;
        };

        addListeners() {
            var that = this,
                applySelection = function(axis, selection) {
                    var opts = axis.options,
                        offsetBelow = selection.from - axis.min,
                        offsetAbove = selection.to - axis.max;

                    if (opts.offset) {
                        opts.offset.below = (opts.offset.below || 0) + offsetBelow;
                        opts.offset.above = (opts.offset.above || 0) + offsetAbove;
                    } else {
                        opts.offset = {
                            above: offsetAbove,
                            below: offsetBelow
                        };
                    }
                };

            this.addTrackedEventHandler(this, 'ni-cartesian-graph-box-selected', function(evt) {
                // a rectangle selection was made on the graph, zoom into that selection
                // info about the selection box is passed in the event details (ranges)
                var ranges = evt.detail.originalSource.ranges;
                var plot = that._parentGraph.graph;

                $.each(plot.getAxes(), function (name, axis) {
                    if (axis.options.disableZoom !== true && ranges[name]) {
                        applySelection(axis, ranges[name]);
                    }
                });

                // redraw plot
                plot.setupGrid();
                plot.draw();
                plot.clearSelection();

                this.updateCartesianAxesFromFlot();
            });

            this.addTrackedEventHandler(this, 'ni-cartesian-graph-interaction', function() {
                this.updateCartesianAxesFromFlot();
            });

            this.addTrackedEventHandler(this, 'ni-axis-reset', function() {
                this.rangeMismatch = true;
            });
        }

        allAutoScaleOn() {
            return this._parentGraph.axes.every(axis => axis.autoScale !== 'none');
        }

        setAutoScaleButtonActiveState() {
            if (this.allAutoScaleOn()) {
                this.toggleAutoScaleButton.classList.add('ni-active-button');
            } else {
                this.toggleAutoScaleButton.classList.remove('ni-active-button');
            }
        }

        attached() {
            super.attached();

            var childElement;

            childElement = document.createElement('div');
            childElement.id = 'div' + NI_SUPPORT.uniqueId();
            childElement.style.width = '100%';
            childElement.style.height = '100%';
            this.appendChild(childElement);
            this.table = document.createElement('table');
            this.table.className = 'ni-graph-tools-box';
            childElement.appendChild(this.table);

            this.createRow(this.table);
            this.updateDisabledState();

            this.addTrackedEventHandler(this, 'ni-cartesian-axis-attached', function (evt) {
                if (this.toggleAutoScaleButton === null) {
                    return;
                }

                this.setAutoScaleButtonActiveState();
            });

            var graph = this.findGraph();
            if (graph !== null && graph instanceof NationalInstruments.HtmlVI.Elements.CartesianGraphBase) {
                this.attachToGraph(graph);
            }
        };

        detached() {
            super.detached();

            this.removeTrackedEventHandlers();
            if (this._parentGraph !== undefined) {
                this._parentGraph.graphTools = undefined;
            }

            this._parentGraph = undefined;
            this.table = undefined;
            $(this).empty();
        }

        /** **asyncProcessAllImagesForComposition** - gathers all the required
        layers of a graph, chart etc (i.e. plot, axes, cursors, legend),
        adds a background color to them and creates a snapshot (as canvas).
        If no background color is specified, it uses the plot background color.
        It returns a promise, which can be used to get the generated snapshot
        image. Using *downloadSnapshot* function, this image can be downloaded
        to disk (see *downloadSnapshot* function below).
        asyncProcessAllImagesForComposition is available, to allow users to
        write their own custom download functions.

    Example:
    ```js
    var asyncSnapshot = function() {
        asyncProcessAllImagesForComposition().then(triggerDownload(), failureCallback);
        _parentGraph.graph.getEventHolder().removeEventListener('onDrawingDone', asyncSnapshot);
    };
    _parentGraph.graph.getEventHolder().addEventListener('onDrawingDone', asyncSnapshot);
    _parentGraph.updateGraphConfig();
    ```
        */
        asyncProcessAllImagesForComposition(userBackgroundColor) {
            var that = this;

            function getDisposeBackgroundSVG(backgroundSVG) {
                return function disposeBackgroundSVG(result) {
                    backgroundSVG.parentNode.removeChild(backgroundSVG);
                }
            }

            function failureCallback() {
            };

            function drawARectangleOnSVG(backgroundSVG, color) {
                var rectangle = document.createElementNS(backgroundSVG, 'rect');
                rectangle.setAttributeNS(null, 'x', 0);
                rectangle.setAttributeNS(null, 'y', 0);
                rectangle.setAttributeNS(null, 'style', 'fill:' + color);
                rectangle.setAttributeNS(null, 'width', backgroundSVG.width.baseVal.value + 'px');
                rectangle.setAttributeNS(null, 'height', backgroundSVG.height.baseVal.value + 'px');
                backgroundSVG.appendChild(rectangle);
            }

            function createBackgroundSVG(backgroundSVG, graphCanvas) {
                const bkBorderWidth = 10,
                    bkBorderWidth2x = bkBorderWidth * 2;

                var pixelRatio = $.plot.browser.getPixelRatio(graphCanvas.getContext('2d'));
                var bkBorderWidth2xScaled = bkBorderWidth2x * pixelRatio;

                document.body.insertBefore(backgroundSVG, document.body.childNodes[0]);

                if (pixelRatio < 1) {
                    backgroundSVG.setAttributeNS(null, 'width', graphCanvas.width / pixelRatio + bkBorderWidth2xScaled);
                    backgroundSVG.setAttributeNS(null, 'height', graphCanvas.height / pixelRatio + bkBorderWidth2xScaled);
                } else {
                    backgroundSVG.setAttributeNS(null, 'width', graphCanvas.width + bkBorderWidth2xScaled);
                    backgroundSVG.setAttributeNS(null, 'height', graphCanvas.height + bkBorderWidth2xScaled);
                }

                backgroundSVG.style.width = graphCanvas.style.width + bkBorderWidth2xScaled;
                backgroundSVG.style.height = graphCanvas.style.height + bkBorderWidth2xScaled;
                backgroundSVG.style.position = 'absolute';

                var svgLeft = graphCanvas.getBoundingClientRect().left + window.scrollX - bkBorderWidth;
                var svgTop = graphCanvas.getBoundingClientRect().top + window.scrollY - bkBorderWidth;
                backgroundSVG.style.left = svgLeft + 'px';
                backgroundSVG.style.top = svgTop + 'px';
            }

            function getSVGFromLegendLayer(graphRef) {
                const containerClassNames = [
                    'ni-light-plot-legend',
                    'ni-chart',
                    'ni-cartesian-graph',
                    'ni-intensity-graph'
                ];
                var result = null;
                var searchQuery = '';
                for (var i = 0; i < containerClassNames.length; i++) {
                    searchQuery = containerClassNames[i] + '[graph-ref="' + graphRef + '"] svg.legendLayer';
                    result = document.querySelector(searchQuery);
                    if ((result !== null) && (result !== undefined)) {
                        break;
                    }
                }
                return result;
            }

            // Get the SVG element for the cursor thumbs
            function getSVGForCursorThumbs(parentGraph) {
                const placeholder = parentGraph.graph.getPlaceholder();

                if (placeholder && placeholder.find('.flot-thumbs')[0]) {
                    return placeholder.find('.flot-thumbs')[0].firstChild;
                }

                return null;
            }

            var graphCanvas = that._parentGraph.graph.getCanvas(),
                backgroundSVG = document.createElementNS('http://www.w3.org/2000/svg', 'svg'),
                graphSVG = that._parentGraph.graph.getSurface().SVGContainer.childNodes[0],
                cursorsFlotOverlay = that._parentGraph.graph.getEventHolder(),
                cursorThumbs = getSVGForCursorThumbs(that._parentGraph),
                legendSVG = getSVGFromLegendLayer(that._parentGraph.graphRef),
                sources,
                snapshotBackgroundColor;

            createBackgroundSVG(backgroundSVG, graphCanvas);
            if (userBackgroundColor !== undefined) {
                snapshotBackgroundColor = userBackgroundColor;
            } else {
                snapshotBackgroundColor = window.getComputedStyle(that._parentGraph).backgroundColor;
            }
            drawARectangleOnSVG(backgroundSVG, snapshotBackgroundColor);

            sources = [backgroundSVG, graphCanvas, graphSVG, cursorsFlotOverlay, cursorThumbs, legendSVG];

            var destinationCanvas = document.createElement('canvas'),
                asynResult = that._parentGraph.takeGraphSnapshot(sources, destinationCanvas);

            return asynResult
                .then(getDisposeBackgroundSVG(backgroundSVG), failureCallback)
                .then(function() {
                    return destinationCanvas;
                }, failureCallback);
        }

        /** **downloadSnapshot** - is a wrapper over *asyncProcessAllImagesForComposition*
        function, used to generate a filename and trigger the actual download of
        a graph snaphot. It also updates the plot dimensions to the page zoom,
        before calling *asyncProcessAllImagesForComposition*. This allows creating
        a high resolution snapshot, simply by zooming the page in.
        Updating the plot dimensions as the page is zoomed in/out, is a requirement,
        to make sure all layers of a snapshot are scaled accordingly.
        As a limitation, it may not be possible to create and download snapshots
        at every refresh of a plot.

    Example:
    ```js
    function takeGraphSnapshotOnClick() {
        downloadSnapshot();
    }
    ```
        */
        downloadSnapshot() {
            var that = this;

            function getCopyCanvasToImg() {
                return function copyCanvasToImgOuter(canvas) {
                    var img = document.createElement('img');
                    img.width = canvas.width;
                    img.height = canvas.height;
                    img.src = canvas.toDataURL('image/png');

                    var downloadBlob = function(blob) {
                        var url = URL.createObjectURL(blob);

                        img.src = url;

                        var downloadLinkElement = document.createElement('A');
                        downloadLinkElement.download = 'Snapshot_' + getDateTimeAsString() + '.png';
                        downloadLinkElement.href = img.src;
                        var clickEvent = new MouseEvent('click');
                        downloadLinkElement.dispatchEvent(clickEvent);
                    };

                    if ($.plot.browser.isEdge()) {
                        downloadBlob(canvas.msToBlob());
                    } else {
                        canvas.toBlob(downloadBlob);
                    }
                    return canvas;
                }
            }

            //function adapted from https://stackoverflow.com/questions/221294/how-do-you-get-a-timestamp-in-javascript
            function getDateTimeAsString() {
                var currentTime = new Date(),
                    year = currentTime.getFullYear(),
                    month = currentTime.getMonth() + 1, //this returns 0-11, so add a 1
                    date = currentTime.getDate(),
                    hours = currentTime.getHours(),
                    minutes = currentTime.getMinutes(),
                    seconds = currentTime.getSeconds();

                if (month < 10) {
                    month = '0' + month;
                }
                if (date < 10) {
                    date = '0' + date;
                }
                if (hours < 10) {
                    hours = '0' + hours;
                }
                if (minutes < 10) {
                    minutes = '0' + minutes;
                }
                if (seconds < 10) {
                    seconds = '0' + seconds;
                }

                var str = year + '.' + month + '.' + date + '_' + hours + '.' + minutes + '.' + seconds;
                return str;
            }

            function failureCallback() {
            };

            var asyncSnapshot = function() {
                that.asyncProcessAllImagesForComposition().then(getCopyCanvasToImg(), failureCallback);
                that._parentGraph.graph.getEventHolder().removeEventListener('onDrawingDone', asyncSnapshot);
            };
            that._parentGraph.graph.getEventHolder().addEventListener('onDrawingDone', asyncSnapshot);

            that._parentGraph.updateGraphConfig();
        }

        createRow(tbl) {
            var that = this;
            var tr, td1, td2, td3, td4, td5, td6, navNone, btnPan, btnZoom, btnZoomOut, btnToggleAutoscale, btnSnapshot;

            function setNavButtonsState(locked, pan, zoom) {
                clearActiveButtonState();
                navNone.disabled = locked;
                if (locked) {
                    navNone.classList.add('ni-active-button');
                }
                btnPan.disabled = pan;
                if (pan) {
                    btnPan.classList.add('ni-active-button');
                }
                btnZoom.disabled = zoom;
                if (zoom) {
                    btnZoom.classList.add('ni-active-button');
                }
            }

            function clearActiveButtonState() {
                navNone.classList.remove('ni-active-button');
                btnPan.classList.remove('ni-active-button');
                btnZoom.classList.remove('ni-active-button');
            }

            function setMode(mode) {
                var states = navigationStates[mode];

                if (states) {
                    setNavButtonsState.apply(null, states);
                    that.mode = mode;
                }
            }

            function takeGraphSnapshotOnClick() {
                that.downloadSnapshot();
            }

            function getAxisId(axis) {
                var id = axis.jqxId !== undefined ? axis.jqxId : axis.label;
                return id;
            }

            function saveThenDisableAutoScaleStates() {
                var graph = that._parentGraph;
                for (var child in graph.childNodes) {
                    var node = graph.childNodes[child];
                    if (node.localName === 'ni-cartesian-axis' || node.localName === 'ni-color-scale') {
                        var id = getAxisId(node);
                        if (id !== undefined) {
                            that.autoScaleStates[id] = node.autoScale;
                            node.savedAutoScale = node.autoScale;
                            node.autoScale = "none";
                        }
                    }
                }
            }

            function getSavedAutoScaleState(axis) {
                var id = getAxisId(axis);
                return that.autoScaleStates[id];
            }

            function purgeSavedAutoScaleState(axis) {
                var id = getAxisId(axis);
                delete that.autoScaleStates[id];
            }

            function restoreAutoScaleStates() {
                var graph = that._parentGraph;
                for (var child in graph.childNodes) {
                    var node = graph.childNodes[child];
                    if (node.localName === 'ni-cartesian-axis' || node.localName === 'ni-color-scale') {
                        var autoScaleSavedState = getSavedAutoScaleState(node);
                        if (autoScaleSavedState !== undefined) {
                            node.autoScale = autoScaleSavedState !== 'none' ? autoScaleSavedState : 'loose';
                        } else if (node.initialAutoScale !== undefined && node.initialAutoScale !== 'none') {
                            node.autoScale = node.initialAutoScale
                        } else {
                            node.autoScale = 'loose';
                        }
                    }
                }

                that.autoScaleStates = {}; // purge saved state
            }

            function resetOffsetOnAllAxes(keepExistingRange) {
                that._parentGraph.axes.forEach(function(axis) {
                    if (axis.resetOffset) {
                        axis.resetOffset(keepExistingRange);
                    }
                });
            }

            that.addTrackedEventHandler(that, 'ni-axis-changed', function (evt) {
                var scale = evt.detail.originalSource;
                if (scale.autoScale !== 'none') {
                    var savedAutoScaleState = getSavedAutoScaleState(scale);
                    if (savedAutoScaleState !== undefined) {
                        // This means that we just changed the autoScale setting of the scale so we need to change
                        // the mode back to locked.
                        setMode(MODE_ENUM.LOCKED);
                        purgeSavedAutoScaleState(scale);
                    }
                }

                that.setAutoScaleButtonActiveState();
            });

            tr = document.createElement('tr');
            tbl.appendChild(tr);

            td1 = document.createElement('td');
            td1.className = 'ni-button-box';
            tr.appendChild(td1);
            navNone = this.uihelpers.createJQXButton('ni-locked-icon ni-button', false);
            this.addTrackedEventHandler(navNone, 'click', function() {
                setMode(MODE_ENUM.LOCKED);
            });
            td1.appendChild(navNone);
            that.lockButton = navNone;

            td2 = document.createElement('td');
            td2.className = 'ni-button-box';
            tr.appendChild(td2);
            btnPan = this.uihelpers.createJQXButton('ni-pan-icon ni-button', false);
            this.addTrackedEventHandler(btnPan, 'click', function() {
                restoreAutoScaleStates();
                saveThenDisableAutoScaleStates();
                resetOffsetOnAllAxes(true);
                setMode(MODE_ENUM.PAN);
            });
            td2.appendChild(btnPan);
            that.panButton = btnPan;

            td3 = document.createElement('td');
            td3.className = 'ni-button-box';
            tr.appendChild(td3);
            btnZoom = this.uihelpers.createJQXButton('ni-zoom-region-icon ni-button', false);
            this.addTrackedEventHandler(btnZoom, 'click', function() {
                restoreAutoScaleStates();
                saveThenDisableAutoScaleStates();
                resetOffsetOnAllAxes(true);
                setMode(MODE_ENUM.ZOOM);
            });
            td3.appendChild(btnZoom);
            that.zoomButton = btnZoom;

            setMode(this.mode); // initialize the "navigation radio butons" in the default mode
            td4 = document.createElement('td');
            td4.className = 'ni-button-box';
            tr.appendChild(td4);
            btnZoomOut = this.uihelpers.createJQXButton('ni-zoom-out-icon ni-button', false);
            btnZoomOut.disabled = false;
            this.addTrackedEventHandler(btnZoomOut, 'click', function() {
                resetOffsetOnAllAxes(false, false);
            });
            td4.appendChild(btnZoomOut);
            that.zoomOutButton = btnZoomOut;

            td5 = document.createElement('td');
            td5.className = 'ni-button-box';
            tr.appendChild(td5);
            btnToggleAutoscale = this.uihelpers.createJQXButton('ni-toggle-autoscale-icon ni-button', false);
            btnToggleAutoscale.disabled = false;
            this.addTrackedEventHandler(btnToggleAutoscale, 'click', function () {
                if (that.allAutoScaleOn()) {
                    saveThenDisableAutoScaleStates();
                } else {
                    setMode(MODE_ENUM.LOCKED);
                    restoreAutoScaleStates();
                }

                that.setAutoScaleButtonActiveState();
                resetOffsetOnAllAxes(true);
            });
            td5.appendChild(btnToggleAutoscale);
            that.toggleAutoScaleButton = btnToggleAutoscale;

            td6 = document.createElement('td');
            td6.className = 'ni-button-box';
            tr.appendChild(td6);
            btnSnapshot = this.uihelpers.createJQXButton('ni-snapshot-icon ni-button', false);
            btnSnapshot.disabled = false;
            this.addTrackedEventHandler(btnSnapshot, 'click', function() {
                takeGraphSnapshotOnClick(td5);
            });
            td6.appendChild(btnSnapshot);
            that.snapshotButton = btnSnapshot;
        };

        allowsScrollWheelZoom() {
            return this.mode === MODE_ENUM.PAN || this.mode === MODE_ENUM.ZOOM;
        };

        allowsPan() {
            return this.mode === MODE_ENUM.PAN;
        };

        sendEventToParentGraph(name) {
            var eventConfig;

            if (this._parentGraph !== undefined) {
                eventConfig = {
                    bubbles: true,
                    cancelable: true,
                    detail: {
                        element: this
                    }
                };

                this._parentGraph.dispatchEvent(new CustomEvent(name, eventConfig));
            }
        };

        updateDisabledState() {
            if (this.lockButton !== null) {
                this.lockButton.disabled = this.disabled;
            }

            if (this.panButton !== null) {
                this.panButton.disabled = this.disabled;
            }

            if (this.zoomButton !== null) {
                this.zoomButton.disabled = this.disabled;
            }

            if (this.zoomOutButton !== null) {
                this.zoomOutButton.disabled = this.disabled;
            }

            if (this.toggleAutoScaleButton !== null) {
                this.toggleAutoScaleButton.disabled = this.disabled;
            }

            if (this.snapshotButton !== null) {
                this.snapshotButton.disabled = this.disabled;
            }
        }

        propertyChangedHandler(propertyName, oldValue, newValue) {
            super.propertyChangedHandler(propertyName, oldValue, newValue);

            switch (propertyName) {
                case 'graphRef':
                    var graphElement = this.findGraph();
                    if (graphElement != null) {
                        this.attachToGraph(graphElement);
                    };
                    break;
                case 'mode':
                    this.sendEventToParentGraph('ni-graph-tools-changed');
                    break;
                case 'disabled':
                    this.updateDisabledState();
                    break;
                default:
                    break;
            }
        };
    };

    JQX('ni-graph-tools', GraphTools);
}());
